import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'

const AuthContext = createContext(null)

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

export function AuthProvider({ children }) {
    const [session, setSession] = useState(null)
    const [user, setUser] = useState(null)
    const [profile, setProfile] = useState(null) // DB Profile
    const [role, setRole] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let mounted = true
        console.log('[AuthProvider] mounting...')

        // 1. 세션 및 프로필 가져오기 (비차단형)
        const initializeAuth = async () => {
            console.log('[AuthProvider] initializeAuth start')

            // 🛡️ [Safe-guard] 3초 후에도 로딩이 안 풀리면 강제로 풀기
            const timeoutId = setTimeout(() => {
                if (mounted && loading) {
                    console.warn('[AuthProvider] initializeAuth timeout hit - forcing loading false')
                    setLoading(false)
                }
            }, 3000)

            try {
                // getSession은 로컬 스토리지에서 즉시 세션을 가져옴 (비동기지만 빠름)
                const { data: { session } } = await supabase.auth.getSession()
                console.log('[AuthProvider] getSession done', session?.user?.email)
                if (!mounted) return

                if (session?.user) {
                    setSession(session)
                    setUser(session.user)

                    // 🛑 [관리자 강제 부여] 특정 이메일은 즉시 권한 부여 (비동기 fetch 전)
                    if (session.user.email === '01050012171@floater.kr') {
                        console.log('[AuthProvider] Admin override detected! (email:', session.user.email, ')')
                        setRole('admin')
                    }

                    // 프로필은 배경에서 로드 (비차단)
                    fetchProfile(session.user)
                } else {
                    console.log('[AuthProvider] No session found')
                    setSession(null)
                    setUser(null)
                    setProfile(null)
                    setRole(null)
                }
            } catch (err) {
                console.error('[AuthProvider] initializeAuth error:', err)
            } finally {
                clearTimeout(timeoutId)
                if (mounted) {
                    console.log('[AuthProvider] initializeAuth finished - setting loading false')
                    setLoading(false)
                }
            }
        }

        // 2. 프로필 데이터 가져오기 (Helpers)
        const fetchProfile = async (currentUser) => {
            if (!currentUser || !mounted) return
            const userId = currentUser.id
            const userEmail = currentUser.email
            console.log('[AuthProvider] fetchProfile start for', userEmail)

            try {
                // Determine if we should upgrade to admin role early
                const isAdminEmail = (userEmail === '01050012171@floater.kr');

                // Fetch Profile Only (Address should now be in profiles)
                const { data: profileData, error: profileError } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('user_id', userId)
                    .maybeSingle()

                if (profileError) console.error('[AuthProvider] fetchProfile error:', profileError)

                if (mounted) {
                    console.log('[AuthProvider] profile data received:', profileData?.role)

                    if (profileData) {
                        setProfile(profileData)
                    }

                    // Role Assignment
                    if (isAdminEmail) {
                        setRole('admin')
                    } else {
                        setRole(profileData?.role || currentUser?.user_metadata?.role || null)
                    }
                }
            } catch (e) {
                console.error('[AuthProvider] fetchProfile exception:', e)
            }
        }

        initializeAuth()

        // 3. Auth 상태 변경 구독
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            console.log('[AuthProvider] onAuthStateChange event:', _event, session?.user?.email)
            if (!mounted) return

            if (session?.user) {
                setSession(session)
                setUser(session.user)
                if (session.user.email === '01050012171@floater.kr') {
                    setRole('admin')
                }
                fetchProfile(session.user)
            } else {
                setSession(null)
                setUser(null)
                setProfile(null)
                setRole(null)
            }
            setLoading(false)
        })

        return () => {
            mounted = false
            subscription.unsubscribe()
        }
    }, [])

    // 강력한 로그아웃 (스토리지 클리어 + 강제 리로드)
    const signOut = async () => {
        try {
            await supabase.auth.signOut()
        } catch (error) {
            console.error('Error signing out:', error)
        } finally {
            // Aggressive Cleanup
            Object.keys(localStorage).forEach((key) => {
                if (key.startsWith('sb-') || key.startsWith('supabase')) {
                    localStorage.removeItem(key)
                }
            })
            localStorage.clear()
            sessionStorage.clear()

            setSession(null)
            setUser(null)
            setProfile(null)
            setRole(null)

            window.location.replace('/')
        }
    }

    const value = {
        session,
        user,
        profile,
        role,         // 'admin', 'pharmacist', 'pharmacy' etc.
        loading,
        signOut,
        refreshProfile: () => fetchProfile(session?.user), // ✅ Expose refresh method
        isAdmin: role === 'admin',
        isPharmacist: role === 'pharmacist',
        isPharmacy: role === 'pharmacy',
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
